<template>
  <div class="legal-page">
    <div class="legal-page__header">
      <h1 class="legal-page__title">Privacy Policy</h1>
      <p class="legal-page__date">Effective Date: January 1, 2024 · Updated October 29, 2026</p>
    </div>

    <div class="legal-page__card">
      <p class="legal-text">
        At Thinking of U, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website, mobile application, and related services.
      </p>

      <h3 class="legal-subhead">1. Information We Collect</h3>
      <p class="legal-text">We may collect the following types of information:</p>
      <ul class="legal-list">
        <li><strong>Personal Information:</strong> When you use our services, we may collect your name, email address, and mailing address. Payment information is securely processed by our third-party payment provider, Stripe — we do not store full payment card details on our servers.</li>
        <li><strong>User Content:</strong> We collect the messages, prompts, and inputs you provide when creating postcards. These may be processed by AI systems (such as OpenAI) to generate content.</li>
        <li><strong>Notes to your assistant:</strong> If you email or forward something to your private desk address, we keep only a note of up to 600 characters taken from it (your own words, and the text of a forwarded message) and file it with the people it names; the original email and any attachments are not stored. Mail to that address is accepted only from the email address on your account.</li>
        <li><strong>Usage Information:</strong> We gather data on how you interact with our service, including features used and preferences selected.</li>
        <li><strong>Device Information:</strong> We may collect technical information such as device type, operating system, browser type, and IP address.</li>
        <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to improve your experience, analyze usage, and support core functionality.</li>
      </ul>

      <h3 class="legal-subhead">2. How We Use Your Information</h3>
      <p class="legal-text">We use your information to:</p>
      <ul class="legal-list">
        <li>Provide, maintain, and improve our services.</li>
        <li>Generate AI-powered postcards based on your inputs.</li>
        <li>Process orders and deliver postcards to recipients.</li>
        <li>Communicate with you about your account, orders, or support requests.</li>
        <li>Monitor usage trends and enhance platform performance.</li>
        <li>Ensure the safety, integrity, and moderation of user-generated content.</li>
      </ul>
      <p class="legal-text">
        Processing may be based on your consent, a contractual obligation, or our legitimate business interests.
      </p>

      <h3 class="legal-subhead">3. Sharing Your Information</h3>
      <p class="legal-text">We do not sell or rent your personal information. We only share your data in these situations:</p>
      <ul class="legal-list">
        <li>With your explicit consent.</li>
        <li>To comply with legal obligations or protect our rights and safety.</li>
        <li>With trusted service providers (e.g., payment processors like Stripe, cloud services like Firebase, AI content providers like OpenAI) who are contractually required to protect your data.</li>
        <li>To prevent fraud or abuse of our platform.</li>
      </ul>
      <p class="legal-text">
        If your data is transferred outside your country, we implement safeguards such as standard contractual clauses or equivalent protections.
      </p>

      <h3 class="legal-subhead">4. Google Calendar and Google Contacts (only if you connect them)</h3>
      <p class="legal-text">
        Members can connect a Google account so the assistant knows when they last saw the people they&#8217;ve added, and when
        they&#8217;re about to. Access is read-only: we cannot create, change, or delete anything in your Google account.
      </p>
      <ul class="legal-list">
        <li><strong>What we read:</strong> events on your primary Google Calendar from the past 90 days and the next 60 days, and the names, email addresses and birthdays in your Google Contacts. We read again once each morning while you stay connected.</li>
        <li><strong>What we keep:</strong> for people you have already added to Thinking of U only &mdash; the date you were with them, the event&#8217;s title (first 60 characters), and a birthday where you hadn&#8217;t entered one. We also remember a short reference to each event so it isn&#8217;t counted twice. If someone appears in two or more meetings but isn&#8217;t one of your people, we may keep their name or email address in a suggestion to you to add them. Everything else read from your calendar and contacts is discarded as soon as it has been read and is never stored.</li>
        <li><strong>How we use it:</strong> only to help the assistant decide when, and whether, to reach the people you&#8217;ve added, and to show you what it learned. The kept details may be sent to the AI provider that runs the assistant (Anthropic) for that purpose only. We do not use Google user data to serve ads, sell it, or use it to train AI models.</li>
        <li><strong>Your control:</strong> you can untick calendar or contacts on Google&#8217;s consent screen, strike out any line the assistant learned, and disconnect at any time from your desk, which withdraws our access at Google. You can also remove access from your Google Account&#8217;s security settings.</li>
        <li><strong>Security:</strong> the credential that lets us read your account is encrypted at rest and is never available to the website or to other users.</li>
      </ul>
      <p class="legal-text">
        Thinking of U&#8217;s use and transfer of information received from Google APIs adheres to the
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener">Google API Services User Data Policy</a>,
        including the Limited Use requirements.
      </p>

      <h3 class="legal-subhead">5. Data Retention</h3>
      <p class="legal-text">
        We retain your data only as long as necessary to provide our services and fulfill legal obligations. You may request deletion of your account and associated data at any time.
      </p>

      <h3 class="legal-subhead">6. Security</h3>
      <p class="legal-text">
        We implement reasonable technical and organizational measures to protect your information, including encryption, access controls, and regular security audits. However, no system is 100% secure, and we cannot guarantee absolute protection.
      </p>

      <h3 class="legal-subhead">7. Your Rights and Choices</h3>
      <p class="legal-text">You have rights regarding your personal data, including the right to:</p>
      <ul class="legal-list">
        <li>Access, correct, or delete your data.</li>
        <li>Withdraw consent where applicable.</li>
        <li>Object to certain types of processing.</li>
        <li>Opt out of marketing communications.</li>
      </ul>
      <p class="legal-text">
        You can manage your data through your account settings or by contacting us at
        <a href="mailto:privacy@thinking-of-u.com">privacy@thinking-of-u.com</a>.
      </p>

      <h3 class="legal-subhead">8. Children's Privacy</h3>
      <p class="legal-text">
        Our service is not intended for use by children under 13. We do not knowingly collect personal information from children without verifiable parental consent. If you believe we have collected such data, please contact us.
      </p>

      <h3 class="legal-subhead">9. Changes to This Policy</h3>
      <p class="legal-text">
        We may update this policy periodically. Significant changes will be communicated via our website or direct notice. Continued use of our service after changes constitutes acceptance of the revised policy.
      </p>

      <h3 class="legal-subhead">10. Contact Us</h3>
      <p class="legal-text">
        If you have any questions about this policy or your data, please contact us at
        <a href="mailto:privacy@thinking-of-u.com">privacy@thinking-of-u.com</a> or visit our
        <router-link to="/contact-us">Contact Us</router-link> page.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
};
</script>

<style scoped>
.legal-page {
  background: linear-gradient(180deg, var(--light-pink) 0%, #f5f0fa 100%);
  min-height: 100vh;
  padding: 0 1rem 3rem;
}

.legal-page__header {
  text-align: center;
  padding: 3rem 1rem 1.5rem;
}

.legal-page__title {
  font-size: 2.2rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.legal-page__date {
  font-size: 0.9rem;
  color: #888;
  font-style: italic;
}

.legal-page__card {
  max-width: 800px;
  margin: 0 auto;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  animation: cardIn 0.5s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.legal-subhead {
  font-size: 1.15rem;
  color: var(--hollywood-cerise);
  margin: 2rem 0 0.75rem;
  font-weight: 600;
}

.legal-text {
  font-size: 0.98rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 1rem;
}

.legal-text a,
.legal-text a:visited {
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 500;
}

.legal-text a:hover {
  text-decoration: underline;
}

.legal-list {
  padding-left: 0;
  margin-bottom: 1rem;
  list-style-position: inside;
}

.legal-list li {
  font-size: 0.98rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 0.5rem;
}

.legal-list li strong {
  color: var(--violet-lush);
}

@media (max-width: 768px) {
  .legal-page__title { font-size: 1.8rem; }
  .legal-page__card { padding: 1.5rem; border-radius: 16px; }
}
</style>
